import type {
  TerraUIMessage,
  TerraUIMessageDataPart,
  TerraUIMessagePart,
  ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import {
  isCoderToolNameForMerge,
  readCoderResultFromStructuredOutput,
} from '../trace-view/utils/coder-tool-output';
import { isDataOrConfirmationPart } from './hitl-confirmation-matching';
import {
  TOOL_CALL_STATUS_PRIORITY,
  isToolCallData,
  mergeToolCallData,
} from './tool-call-data-merge';

type ToolCallDataPart = TerraUIMessageDataPart & {
  data: ToolCallGenericMessage;
};

type ToolCallTimeline = {
  anchorIndex: number;
  latestPart: ToolCallDataPart;
  priority: number;
  data: ToolCallGenericMessage;
};

function readToolCallDataPart(
  part: TerraUIMessagePart,
): ToolCallDataPart | undefined {
  if (!isDataOrConfirmationPart(part)) return undefined;

  const data = part.data;

  if (!data || !isToolCallData(data) || !data.toolCallId) return undefined;

  return part as ToolCallDataPart;
}

function readCoderToolOutputRichness(data: ToolCallGenericMessage): number {
  if (!isCoderToolNameForMerge(data.toolName)) {
    return 0;
  }

  return readCoderResultFromStructuredOutput(data.output)?.length ?? 0;
}

function shouldReplaceSupersededToolCall({
  nextPriority,
  currentPriority,
  nextData,
  currentData,
}: {
  nextPriority: number;
  currentPriority: number;
  nextData: ToolCallGenericMessage;
  currentData: ToolCallGenericMessage;
}): boolean {
  if (nextPriority > currentPriority) {
    return true;
  }

  if (nextPriority < currentPriority) {
    return false;
  }

  if (!isCoderToolNameForMerge(nextData.toolName)) {
    return true;
  }

  return (
    readCoderToolOutputRichness(nextData) >=
    readCoderToolOutputRichness(currentData)
  );
}

function advanceToolCallTimeline({
  timeline,
  part,
}: {
  timeline: ToolCallTimeline;
  part: ToolCallDataPart;
}): ToolCallTimeline {
  const priority = TOOL_CALL_STATUS_PRIORITY[part.data.status] ?? -1;

  if (
    shouldReplaceSupersededToolCall({
      nextPriority: priority,
      currentPriority: timeline.priority,
      nextData: part.data,
      currentData: timeline.data,
    })
  ) {
    return {
      anchorIndex: timeline.anchorIndex,
      latestPart: part,
      priority,
      data: mergeToolCallData({ anchor: timeline.data, update: part.data }),
    };
  }

  if (isCoderToolNameForMerge(part.data.toolName)) {
    return {
      ...timeline,
      data: mergeToolCallData({ anchor: timeline.data, update: part.data }),
    };
  }

  return timeline;
}

export function filterSupersededToolCalls(
  parts: TerraUIMessage['parts'],
): TerraUIMessage['parts'] {
  const timelines = parts.reduce((byToolCallId, part, index) => {
    const toolCallPart = readToolCallDataPart(part);

    if (!toolCallPart) return byToolCallId;

    const toolCallId = toolCallPart.data.toolCallId;
    const timeline = byToolCallId.get(toolCallId);

    byToolCallId.set(
      toolCallId,
      timeline
        ? advanceToolCallTimeline({ timeline, part: toolCallPart })
        : {
            anchorIndex: index,
            latestPart: toolCallPart,
            priority: TOOL_CALL_STATUS_PRIORITY[toolCallPart.data.status] ?? -1,
            data: toolCallPart.data,
          },
    );

    return byToolCallId;
  }, new Map<string, ToolCallTimeline>());

  return parts.flatMap((part, index) => {
    const toolCallPart = readToolCallDataPart(part);

    if (!toolCallPart) return [part];

    const timeline = timelines.get(toolCallPart.data.toolCallId);

    if (!timeline || timeline.anchorIndex !== index) return [];

    return [
      timeline.latestPart.data === timeline.data
        ? timeline.latestPart
        : { ...timeline.latestPart, data: timeline.data },
    ];
  });
}
